import Fuse from 'fuse.js'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'

import arriereBackoffice, {
  HomeSorting,
  IDate,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { getDistanceInMiles } from '@avantstay/backoffice-core'

import { TAPECHART_CACHE_TIMES } from '@new/__global/components/FastTapeChart/config/requestCacheTimes'
import { SortOrderKey } from '@new/__global/components/FiltersFramework'
import { noEciLco, VALID_BOOKING_STATUSES } from '@new/__global/constants/calendar'
import { cachedRequest, cachedRequestWhen, shareInFlight } from '@new/__global/utils/requestCache'

const BOOKING_AND_READINESS_FILTERS = [
  'checkIn',
  'checkOut',
  'checkInExistsFor',
  'operationReadinessStatus',
]

const hasNoBookingOrReadinessFilter = ({ filters }: { filters: Record<string, unknown> }) =>
  BOOKING_AND_READINESS_FILTERS.every(filter => isEmpty(filters[filter]))

export const currencyFormatter = new Intl.NumberFormat('en-us', {
  currency: 'USD',
  style: 'currency',
  maximumFractionDigits: 0,
})

export const arriere = {
  fetchBookings: shareInFlight(
    async ({
      startDate,
      endDate,
      homeIds,
      filters,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
      filters?: Record<string, any>
    }) => {
      const { results: bookings } = await arriereBackoffice.queries.bookings({
        __args: {
          from: startDate,
          to: endDate,
          status: VALID_BOOKING_STATUSES,
          homeIds,
          pagination: { page: 1, size: 99999 },
          ...filters,
        },
        results: {
          id: true,
          source: true,
          homeId: true,
          guestName: true,
          notes: true,
          status: true,
          activeServicesCount: true,
          issues: true,
          securityDepositStatus: true,
          folio: {
            summary: {
              paymentBalanceStatus: true,
            },
          },
          checkInTiming: {
            originalTime: true,
            effectiveTime: true,
          },
          checkOutTiming: {
            originalTime: true,
            effectiveTime: true,
          },
        },
      })

      return orderBy(bookings, it => it.checkInTiming.effectiveTime)
    },
  ),

  fetchBlockedPeriods: shareInFlight(
    async ({
      startDate,
      endDate,
      homeIds,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
    }) => {
      const { searchAvailabilityBlocks } = await arriereBackoffice.queries.tapeChart({
        __alias: 'tapeChartBlockedPeriods',
        __args: {
          period: {
            start: startDate,
            end: endDate,
          },
          homeIds,
        },
        searchAvailabilityBlocks: {
          id: true,
          homeId: true,
          period: { start: true, end: true },
          source: true,
          createdAt: true,
          createdBy: true,
          updatedAt: true,
          updatedBy: true,
          note: true,
          notificationSetting: {
            notifyAuthor: true,
            notifyRelocationTeam: true,
            notifyFieldOpsTeam: true,
          },
          parent: {
            bookingId: true,
            id: true,
            homeId: true,
            period: {
              start: true,
              end: true,
            },
            source: true,
          },
          expiresAt: true,
          booking: {
            detailedCheckIn: {
              effectiveTime: true,
            },
            detailedCheckOut: {
              effectiveTime: true,
            },
            hash: true,
            guestName: true,
            home: {
              name: true,
            },
          },
        },
      })

      return uniqBy(
        searchAvailabilityBlocks,
        it => `${it.homeId},${it.period.start},${it.period.end},${it.source},${it.note}`,
      ).filter(noEciLco)
    },
  ),

  fetchHomes: async ({
    terms,
    page = 1,
    pageSize = 100,
    filters,
    sortBy,
    sortOrder,
  }: {
    terms: string
    page?: number
    pageSize?: number
    filters: Record<string, any>
    sortBy?: string
    sortOrder?: SortOrderKey
  }) => {
    const { homesList, total } = await arriere.fetchTapechartHomes({
      terms,
      page,
      pageSize,
      filters,
    })

    const mappedHomeList = homesList?.map(it => ({
      ...it,
      terms: [
        it.region?.name,
        it.name,
        ...(it.region?.name || '').split(/\s+/),
        ...it.name.split(/\s+-\s+/),
        ...it.name.split(/[\s-]+/),
      ],
    }))

    const { searchableList, rawList } = {
      rawList: mappedHomeList,
      searchableList: new Fuse(mappedHomeList, {
        keys: ['terms'],
        shouldSort: false,
        threshold: 0.25,
        location: 0,
        distance: 3,
      }),
    }

    const results = !terms
      ? rawList
      : searchableList
          .search({
            $or: [
              { terms: terms.trim() },
              {
                $and: terms
                  .trim()
                  .split(/\W+/)
                  .map(it_1 => ({ terms: it_1 })),
              },
            ],
          })
          .map(it_2 => it_2.item)

    const sortedResults =
      sortBy && sortOrder ? orderBy(results, [`home.${sortBy}`], [sortOrder]) : results

    return {
      total,
      results: sortedResults,
      sortedRawResults: sortedResults,
      homesList,
    }
  },

  // this one is used only to find property page number when deep linking into tapechart
  // keep it lightweight
  fetchTapechartHomeIds: cachedRequestWhen({
    request: async ({
      terms,
      page = 1,
      pageSize = 5000,
      filters,
    }: {
      terms?: string
      page?: number
      pageSize?: number
      filters: Record<string, any>
    }) => {
      const { results: homes, total } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'tapeChartHomeIds',
        __args: {
          ...filters,
          name: terms,
          pagination: {
            page,
            size: pageSize,
          },
          sortBy: HomeSorting.regionName,
        },
        total: true,
        results: {
          id: true,
        },
      })

      return {
        homesList: homes,
        total,
      }
    },
    maxAge: TAPECHART_CACHE_TIMES.homes,
    shouldCache: hasNoBookingOrReadinessFilter,
  }),

  fetchTapechartHomes: cachedRequestWhen({
    request: async ({
      terms,
      page = 1,
      pageSize = 30,
      filters,
    }: {
      terms?: string
      page?: number
      pageSize?: number
      filters: Record<string, any>
    }) => {
      const { results: homes, total } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'tapeChartHomes',
        __args: {
          ...filters,
          name: terms,
          pagination: {
            page,
            size: pageSize,
          },
          sortBy: HomeSorting.regionName,
        },
        total: true,
        results: {
          id: true,
          name: true,
          region: {
            id: true,
            name: true,
          },
          home: {
            latitude: true,
            longitude: true,
            revenueModel: true,
            maxOccupancy: true,
            bedroomsCount: true,
            bathroomsCount: true,
            hotelRoomType: {
              hotelRoomTypeId: true,
            },
            petPolicy: true,
          },
          guestReadinessStatus: true,
          lifecycle: {
            riskLevel: true,
          },
        },
      })

      const homesListWithDistance = homes?.map(homeResult => ({
        ...homeResult,
        home: {
          ...homeResult.home,
          distance:
            !!filters.addressRadius && homeResult.home?.latitude && homeResult.home?.longitude
              ? getDistanceInMiles({
                  latA: filters.addressRadius?.pointOfOrigin?.latitude,
                  latB: homeResult.home?.latitude,
                  longA: filters?.addressRadius?.pointOfOrigin?.longitude,
                  longB: homeResult.home?.longitude,
                })
              : undefined,
        },
      }))

      return {
        homesList: homesListWithDistance,
        total,
      }
    },
    maxAge: TAPECHART_CACHE_TIMES.homes,
    shouldCache: hasNoBookingOrReadinessFilter,
  }),

  fetchHomesListExtendedInfo: cachedRequest({
    request: async ({ homeIds }: { homeIds: string[] }) => {
      const { results: homes } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'fetchHomesListExtendedInfo',
        __args: {
          homeIds,
          pagination: {
            page: 1,
            size: 9999,
          },
        },
        results: {
          id: true,
          home: {
            name: true,
            operationReadinessStatus: {
              clean: true,
              inspected: true,
              othersCases: true,
              othersCasesDetails: {
                cases: {
                  isHighOrUrgentPriority: true,
                  workOrderId: true,
                },
                completed: {
                  workOrderId: true,
                },
                status: true,
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              cleanDetails: {
                status: true,
                cases: {
                  isHighOrUrgentPriority: true,
                  workOrderId: true,
                },
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              inspectedDetails: {
                status: true,
                cases: {
                  isHighOrUrgentPriority: true,
                  workOrderId: true,
                },
              },
              status: true,
            },
            operationTags: {
              currentBookingId: true,
              short: true,
            },
            region: {
              id: true,
            },
            blockedHomes: {
              id: true,
            },
          },
        },
      })

      return homes
    },
    maxAge: TAPECHART_CACHE_TIMES.readiness,
  }),

  fetchRates: cachedRequest({
    request: async ({
      startDate,
      endDate,
      homeIds,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
    }) => {
      const { searchHomeDatedPrices: rates } = await arriereBackoffice.queries.tapeChart({
        __alias: 'tapeChartRates',
        __args: {
          period: {
            start: startDate,
            end: endDate,
          },
          homeIds,
        },
        searchHomeDatedPrices: {
          date: true,
          homeId: true,
          minStay: true,
          maxStay: true,
          rate: true,
          checkInAllowed: true,
          checkOutAllowed: true,
        },
      })

      return groupBy(rates, it => it.homeId)
    },
    maxAge: TAPECHART_CACHE_TIMES.rates,
  }),

  fetchHomeCheckInStatus: async ({
    homeIds,
    updatedSince,
  }: {
    homeIds: string[]
    updatedSince?: IDate
  }) => {
    const { results: homes } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
      __alias: 'fetchHomeCheckInStatus',
      __args: {
        homeIds,
        pagination: {
          page: 1,
          size: 200,
        },
      },
      results: {
        id: true,
        home: {
          operationReadinessStatus: {
            __args: { updatedSince },
            status: true,
            othersCasesDetails: {
              cases: {
                isHighOrUrgentPriority: true,
                workOrderId: true,
              },
              completed: {
                workOrderId: true,
              },
              status: true,
              total: true,
            },
            cleanDetails: {
              status: true,
              cases: {
                isHighOrUrgentPriority: true,
                workOrderId: true,
              },
              total: true,
              referencePeriod: {
                end: true,
                start: true,
              },
            },
            inspectedDetails: {
              status: true,
              cases: {
                isHighOrUrgentPriority: true,
                workOrderId: true,
              },
              total: true,
            },
            metadata: {
              referencePeriod: {
                end: true,
                start: true,
              },
            },
            updatedAt: true,
            statusUpdatedAt: true,
          },
        },
      },
    })

    return homes
  },
}
